import { ContractContributorResponse, ContractPermissionLevel } from 'compliance-api/generated';
import { FormikProvider, useFormik } from 'formik';
import { useMemo } from 'react';
import * as yup from 'yup';
import UsersSelectPaginated from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import {
  useCreateContractContributorMutation,
  useUpsertContractContributorMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import {
  BaseModalProps,
  Button,
  ModalContentWrapper,
  Select,
  SelectOption,
  SelectPaginateOption,
} from 'ui';
import { fixNameEnum } from 'ui/utils';

interface ContractContributorModalProps extends BaseModalProps {
  contractId: number;
  contributor?: ContractContributorResponse;
}

type ContractContributorFormValues = {
  user_id?: number;
  userOption?: SelectPaginateOption<number> | null;
  permission_level: 'editor' | 'viewer';
};

const permissionOptions = [
  { value: 'editor' as const, label: fixNameEnum('editor') },
  { value: 'viewer' as const, label: fixNameEnum('viewer') },
] satisfies SelectOption<'editor' | 'viewer'>[];

const getValidationSchema = (isEdit: boolean) =>
  yup.object<ContractContributorFormValues>({
    user_id: isEdit ? yup.number().nullable() : yup.number().required('User is required'),
    permission_level: yup.mixed<'editor' | 'viewer'>().required('Permission is required'),
  });

const getInitialValues = (
  contributor?: ContractContributorResponse,
): ContractContributorFormValues =>
  contributor
    ? {
        user_id: contributor.user_id,
        userOption: {
          value: contributor.user_id,
          label: `${contributor.first_name} ${contributor.last_name}`.trim(),
        },
        permission_level:
          contributor.permission_level === ContractPermissionLevel.Editor ? 'editor' : 'viewer',
      }
    : {
        user_id: undefined,
        userOption: null,
        permission_level: 'viewer',
      };

const ContractContributorModal = ({
  contractId,
  contributor,
  open,
  onClose,
}: ContractContributorModalProps) => {
  const isEdit = !!contributor;

  const formik = useFormik<ContractContributorFormValues>({
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: getInitialValues(contributor),
    validationSchema: useMemo(() => getValidationSchema(isEdit), [isEdit]),
    onSubmit: async values => {
      if (isEdit) {
        if (!contributor) return;

        await updateContractContributor({
          contractId,
          contributorId: contributor.id,
          data: {
            permission_level: values.permission_level,
          },
        });
      } else {
        if (!values.user_id) return;

        await createContractContributor({
          contractId,
          data: {
            user_id: values.user_id,
            permission_level: values.permission_level,
          },
        });
      }

      onClose();
    },
  });

  const { mutateAsync: createContractContributor, isLoading: isCreating } =
    useCreateContractContributorMutation(formik.setErrors);
  const { mutateAsync: updateContractContributor, isLoading: isUpdating } =
    useUpsertContractContributorMutation(formik.setErrors);

  const isSubmitting = isCreating || isUpdating;

  return (
    <ModalContentWrapper
      title={isEdit ? 'Edit contributor' : 'Add contributor'}
      open={open}
      onClose={onClose}
      classes={{ dialogPanel: 'max-w-[520px]' }}
    >
      <FormikProvider value={formik}>
        <div className="space-y-6">
          {isEdit ? (
            <div className="rounded border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-sm font-medium text-gray-900">
                {contributor?.first_name} {contributor?.last_name}
              </div>
              <div className="mt-1 text-sm text-gray-500">{contributor?.email}</div>
            </div>
          ) : (
            <UsersSelectPaginated
              enableCreation={true}
              enableCreationLabel="Invite new user to platform"
              isMulti={false}
              label="User"
              placeholder="Select user"
              value={formik.values.userOption || null}
              onChange={value => {
                const option = value as SelectPaginateOption<number> | null;
                void formik.setFieldValue('user_id', option?.value);
                void formik.setFieldValue('userOption', option);
              }}
              isClearable
              error={formik.touched.user_id ? (formik.errors.user_id as string) : undefined}
            />
          )}

          <Select<'editor' | 'viewer'>
            label="Permission"
            options={permissionOptions}
            value={formik.values.permission_level}
            onChange={value => formik.setFieldValue('permission_level', value)}
            showBorderBottom
          />

          <div className="flex justify-end">
            <Button className="w-fit px-4" onClick={formik.submitForm} loading={isSubmitting}>
              {isEdit ? 'Save' : 'Confirm'}
            </Button>
          </div>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default ContractContributorModal;
